import { Component, OnDestroy } from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CustomToastService } from './custom-toast.service';
import {
  toastIcons,
  triggerChildAnimation,
  verticalCollapseAnimation,
  verticalExpand,
} from './toast-constants';
import { ToastButton, ToastData, ToastLink } from './toast-interfaces';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-custom-toast',
  templateUrl: './custom-toast.component.html',
  styleUrls: ['./custom-toast.component.scss'],
  animations: [
    trigger('slideIn', [
      state(
        '*', //'*' is wildcard which represents all possible state of the element
        style({
          transform: 'translateX(0) scale(1)', //Final position and size
          opacity: 1, // Fully visible
        }),
      ),
      state(
        'void', // When the element is not present(Initial state)
        style({
          transform: 'translateX(-25%) scale(1.1)', // Slightly left and larger
          opacity: 0, // Fully transparent
        }),
      ),
      transition('void => *', animate('.3s ease-in-out')), //Transition in
    ]),

    trigger('slideOut', [
      state(
        '*',
        style({
          transform: 'translateX(0)  scale(1.1)', // Initial position and slightly larger
          opacity: 1, // Fully visible
        }),
      ),
      state(
        'void', // When the element is removed
        style({
          transform: 'translateX(100%) scale(.7)', // Slide out to the right and shrink
          opacity: 0, // Fully transparent
        }),
      ),
      transition('* => void', animate('.3s ease-in-out')), // Transition out
    ]),
    verticalCollapseAnimation,
    triggerChildAnimation,
    verticalExpand,
  ],
})
export class CustomToastComponent implements OnDestroy {
  toastList: ToastData[] = [];
  private toastServiceSubscription: Subscription;

  constructor(public toastService: CustomToastService) {
    // Subscribe to the toast service for incoming toast data
    this.toastServiceSubscription = this.toastService.open.subscribe(
      (toastData) => {
        toastData.forEach((toast) => {
          toast['icon'] = toast?.icon || toastIcons[toast?.status];
        });
        this.toastList = [...toastData];

        // Schedule auto-close for the newly added toast
        const newlyAddedToast = this.toastList?.[0];
        if (newlyAddedToast?.duration > 0) {
          this.scheduleAutoClose(
            newlyAddedToast?.duration,
            newlyAddedToast?.id,
          );
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.toastServiceSubscription.unsubscribe();
  }

  // Close the toast at the specified index
  onClose(index: number): void {
    this.toastService.hide(index);
  }

  /** @deprecated */
  onLinkClick(linkObj, index) {
    this.onClose(index);
    this.toastService.onButtonClick(linkObj);
  }

  // Handle the click on a toast action (button or link)
  onToastActionClick(item: ToastButton | ToastLink, index: number): void {
    const closeToast = item?.closeToast !== false;
    if (closeToast) {
      this.onClose(index);
    }
    this.toastService.notifyToastActionClick(item);
  }

  // Schedule auto-close for a toast with the specified duration and id
  scheduleAutoClose(duration: number, id: string): void {
    setTimeout(() => {
      const index = this.toastList.findIndex((toast) => toast?.id === id);
      if (index !== -1) {
        this.onClose(index);
      }
    }, duration);
  }
}
